import {
  Callout,
  GuideFlow,
  GuidePage,
  GuideSection,
  GuideSteps,
  GuideSubsection,
  GuideTable,
  GuideToc,
  Ui,
} from './GuideParts';

/**
 * The Super Admin master manual, at /admin/guide, behind RequireSuperAdmin.
 *
 * Scope rule, and it is the whole reason this is a separate page rather than a
 * section of the team guide: public/guide.html is a STATIC file. Cloudflare
 * serves it before React or any session exists, so it is readable by anyone
 * with the link, signed in or not. Every sentence about the master key that
 * used to live there was therefore published. This page is where that material
 * belongs, and the team guide has a test that keeps it from drifting back.
 *
 * What is deliberately not written here: nothing that would BREAK anything if
 * it leaked. A route guard is a convenience, not Row Level Security, and prose
 * has no RLS. Everything below describes behaviour the database enforces
 * whether or not the reader knows about it.
 */

const TOC = [
  { id: 'what', label: 'What the master key actually is' },
  { id: 'reach', label: 'What it opens' },
  { id: 'shut', label: 'What stays shut, permanently' },
  { id: 'mimic', label: 'Mimic Mode: viewing as a supplier' },
  { id: 'writing', label: 'Test writing, and why it is off by default' },
  { id: 'audit', label: 'Audit protection' },
  { id: 'care', label: 'Operating notes and known traps' },
];

export function AdminGuidePage() {
  return (
    <GuidePage
      eyebrow="Confidential · Super Admin only"
      title="Master Manual"
      intro="What the master key opens, what it deliberately does not, and how to look at the Hub as somebody else without becoming them."
    >
      <Callout tone="warn">
        This page is gated on <Ui>is_super_admin()</Ui> and is not linked from the team guide.
        Everything on it describes behaviour the database enforces on its own — it holds no
        credential and no bypass — but it names internal mechanics, so keep it inside the team.
      </Callout>

      <GuideToc entries={TOC} />

      <GuideSection id="what" title="1. What the master key actually is">
        <p>
          You are a super admin if you hold a <Ui>super_admin</Ui> level on{' '}
          <strong>any single module</strong> in the permissions matrix, your profile is active, and
          your account is a TAP account. One row anywhere makes you one everywhere; there is no
          separate flag to set.
        </p>
        <p>
          The account-type check matters more than it looks. Without it, a supplier account holding
          a <Ui>super_admin</Ui> row would have inherited every override below — the master key to
          the whole Hub, handed to an external party by a mis-set dropdown. Nothing grants that
          today, and now nothing can.
        </p>
        <Callout>
          The brief for this feature asked for the key to be honoured &ldquo;across all RLS policy
          queries&rdquo;. Taken literally that is around 150 policies edited by hand, each a chance
          to fat-finger a predicate on a table nobody re-reads for a year — and every policy written
          afterwards would have to remember. Instead, six membership helper functions were changed.
          Every policy that calls them inherited the key, including policies that do not exist yet.
        </Callout>
      </GuideSection>

      <GuideSection id="reach" title="2. What it opens">
        <GuideSubsection title="Navigation and module gates">
          <p>
            The 28 module gates run through <Ui>check_user_permission()</Ui>, which short-circuits
            for a super admin — so every module appears in your sidebar and every module-gated
            action is allowed, regardless of what the matrix says for you specifically. The client
            mirrors this: the navigation helpers treat a super admin as permitted everywhere, so a
            module cannot be hidden from the one account the database would have let through.
          </p>
        </GuideSubsection>
        <GuideSubsection title="The six membership helpers">
          <p>
            Underneath the module gates sit the helpers that answer &ldquo;is this person in this
            group&rdquo;. Each keeps its original rule and adds one disjunct, so the ordinary path
            stays readable and the override is one line you can point at.
          </p>
          <GuideTable
            headers={['Helper', 'Ordinarily true for', 'What the key adds']}
            rows={[
              [
                'is_tap_admin()',
                'An active TAP profile with the tap_admin internal role',
                'Admin-only writes across every module',
              ],
              [
                'is_project_member(project_id)',
                'Somebody on that project’s member list',
                'Every project, without being added to it',
              ],
              [
                'is_sales_member()',
                'The sales membership list',
                'Outbound, Deals and everything gated on sales',
              ],
              ['is_cg_member()', 'The CG membership list', 'CG Orders, including PO generation'],
              [
                'is_active_supplier_of(org)',
                'An active supplier account of that organisation',
                'Every supplier organisation — this is what makes Mimic Mode possible',
              ],
              [
                'belongs_to_supplier_organization(org)',
                'A member of that supplier organisation',
                'The same reach, for the tables keyed the other way',
              ],
            ]}
          />
        </GuideSubsection>
        <Callout tone="warn">
          <Ui>is_active_supplier_of()</Ui> returning true for every organisation has a consequence
          worth internalising: any query that trusted RLS to narrow it to a single supplier row now
          gets <em>all</em> of them. A query written as &ldquo;fetch my organisation&rdquo; silently
          becomes &ldquo;fetch whichever came back first&rdquo;. This has already caused one real
          bug. Row Level Security decides what you <em>may</em> read, never what you <em>meant</em>{' '}
          to read — a query that leans on a policy to scope it breaks precisely for whoever the
          policy scopes least, which is you.
        </Callout>
      </GuideSection>

      <GuideSection id="shut" title="3. What stays shut, permanently">
        <p>
          Three things do not yield to the master key, by design rather than by omission. Two of
          them are enforced by triggers rather than policies, so a wider door never reached them in
          the first place.
        </p>
        <GuideTable
          headers={['What', 'How it is enforced', 'Why']}
          rows={[
            [
              'Project and social activity history',
              'BEFORE triggers that refuse every update and delete, including from the table owner',
              'A log a super admin can rewrite is not a log, it is a draft. There is no client update path at all.',
            ],
            [
              'Task and CG activity history',
              'The same append-only triggers on their own tables',
              'Same reasoning: history is the one thing that must survive whoever is holding the keys.',
            ],
            [
              'Sample decisions',
              'decide_supplier_submission(), plus a unique index allowing one winning option per component',
              'A wider door still walks through the same decision function. You cannot hand-approve two options into one slot.',
            ],
            [
              'Your own profile while mimicking',
              'Nothing writes to it — Mimic Mode is a client-side view state',
              'Your account_type, role and organisation are exactly what they were before and after.',
            ],
          ]}
        />
      </GuideSection>

      <GuideSection id="mimic" title="4. Mimic Mode: viewing as a supplier">
        <p>
          Mimic Mode lets you open the supplier portal as a chosen factory, from your own login. No
          burner account, no password sharing, nothing changed on your profile.
        </p>
        <GuideSteps
          steps={[
            <>
              Go to <strong>/supplier</strong> directly. There is still no link to the portal itself
              in the TAP sidebar — it is not a TAP page, and you reach it by URL. (The{' '}
              <Ui>🏭 Supplier Portal Guide</Ui> in your <Ui>📖 Guides &amp; Manuals</Ui> popover is
              the supplier&rsquo;s manual, not the portal.)
            </>,
            <>
              A bar appears across the top of the portal reading <Ui>🛡️ Mimic Mode</Ui>. It only
              renders for a super admin on a TAP account; a real supplier never sees it.
            </>,
            <>
              Use <Ui>Viewing as</Ui> to choose the organisation. The portal below re-queries and
              shows that supplier&rsquo;s real data — their handoffs, submissions, reviews and
              files, exactly as they see them.
            </>,
            <>
              To leave, navigate away. There is no exit button because there is no state to unwind.
            </>,
          ]}
        />
        <GuideFlow
          label="What Mimic Mode does and does not touch"
          steps={['Your login', 'Chosen org carried', 'Portal queries', 'Your profile: untouched']}
        />
        <Callout>
          The chosen organisation is <strong>carried, never inferred</strong>. Every portal query
          that needs an organisation is passed the one you picked, explicitly. This is not
          decoration: because the master key makes you a valid supplier of every organisation,
          anything that instead read &ldquo;whichever row came back&rdquo; would show you a
          different factory than the banner claims. If you add a portal query, pass the effective
          organisation into it and put it in the query key.
        </Callout>
      </GuideSection>

      <GuideSection id="writing" title="5. Test writing, and why it is off by default">
        <p>
          Mimic Mode starts <strong>read-only</strong>. Uploads, submissions and acknowledgements
          are hidden until you switch <Ui>⚡ Enable test writing</Ui> on. The badge beside it tells
          you which mode you are in: <Ui>Read-only</Ui>, or <Ui>Writes land as you</Ui>.
        </p>
        <p>
          The badge is the honest description. A write made while mimicking is recorded against{' '}
          <em>your</em> user id while being presented in the interface as a supplier action — and
          the review flow reads who did what to decide what happened. An acknowledgement you leave
          behind while testing is indistinguishable, to everyone downstream, from the factory having
          acknowledged it.
        </p>
        <Callout tone="stop">
          Never leave test writing on after you finish. Turn it on deliberately, do the one thing
          you needed to test, turn it off. If you do write something by accident, say so in the
          review conversation immediately — it cannot be told apart later.
        </Callout>
        <p>
          Reasonable uses: confirming an upload path actually works end to end, reproducing a
          supplier&rsquo;s bug report, checking a slot reopens after a revision. Unreasonable ones:
          acknowledging a handoff on a factory&rsquo;s behalf to move a project along, or approving
          your own test submission to clear a queue.
        </p>
      </GuideSection>

      <GuideSection id="audit" title="6. Audit protection">
        <p>Three guarantees hold regardless of who is signed in.</p>
        <GuideTable
          headers={['Guarantee', 'What it means in practice']}
          rows={[
            [
              'Zero profile mutation',
              'Mimicking writes nothing to any profile — not yours, not the supplier’s. There is no impersonation record to clean up because no identity was swapped; you were always you, looking at their data.',
            ],
            [
              'Immutable activity logs',
              'Project, task, social and CG activity refuse updates and deletes at the trigger level, above the policy layer, for every role including the table owner. Whatever a super admin does is still written down, and cannot be unwritten.',
            ],
            [
              'Decisions stay in the decision function',
              'Approvals, revisions and holds go through decide_supplier_submission() no matter who calls it, so the audit trail of a decision is identical whether an admin or a super admin made it.',
            ],
          ]}
        />
        <p>
          The practical upshot: the master key widens what you can <em>see and change</em>, and
          narrows nothing about what gets <em>recorded</em>. Assume everything you do is legible
          afterwards, because it is.
        </p>
      </GuideSection>

      <GuideSection id="care" title="7. Operating notes and known traps">
        <GuideTable
          headers={['Trap', 'What happens', 'What to do']}
          rows={[
            [
              'Granting a second super admin',
              'scripts/permissions-matrix-assertions.sql asserts that exactly one super admin exists. It goes red the day a second is granted, and the failure will look like an unrelated regression.',
              'Fix the assertion before granting, not after — a red gate that everyone has learned to ignore is worse than no gate.',
            ],
            [
              'Cold-load flicker on gated pages',
              'is_super_admin() reads a permissions query that has not answered yet, so it is false for a moment for everybody. A gate that decides during that moment locks you out of your own pages intermittently.',
              'Any new super-admin gate must wait on the permissions query before deciding. This page and the supplier boundary both do.',
            ],
            [
              'Unfiltered “my organisation” queries',
              'With the master key, they return every organisation and quietly pick the first.',
              'Filter explicitly. Never rely on a policy to return exactly one row for you.',
            ],
            [
              'Reading your own permissions row',
              'An unfiltered read of user_module_permissions returns everyone’s rows for a super admin, and the last one wins — which once turned the owner’s Admin level into somebody else’s no_access.',
              'The query filters by user id explicitly. Leave that filter alone.',
            ],
          ]}
        />
        <Callout>
          The team guide at <strong>/guide</strong> deliberately contains none of this, and a test
          in the repo fails if the words find their way back into it. That guide is a static file
          served to anyone with the link — including people who have never signed in — so treat
          anything written there as public.
        </Callout>
      </GuideSection>
    </GuidePage>
  );
}
